/**
 * Profile photos in a call (Robert, 2026-09-28).
 *
 * The server hands out signed, expiring links to a person's photo only to
 * someone calling them or being called by them (vrs-server/lib/profile-photos.js):
 * `callerPhotoUrl` on an incoming ring, `calleePhotoUrl` when your own call
 * rings. A link is a path on the API origin; the web uses it as it is, the
 * apps put their API origin in front (getApiOrigin).
 *
 * The ring is not the room: a call's photo is remembered here from the ring
 * (or the phone's system ring, when that is what was answered) until the
 * call is entered and stored with it (`peerPhotoUrl`), which is where the
 * waiting screen reads it.
 */
const PHOTO_PATH = /^\/api\/profile-photos\/[A-Za-z0-9_-]{1,128}\?[A-Za-z0-9=&%._~-]{1,512}$/;
const E164 = /^\+[1-9][0-9]{6,14}$/;
const MAX_REMEMBERED = 20;
const callPhotos = new Map();
/**
 * A profile photo link as the server signs it, or undefined.
 *
 * @param {unknown} value - A field from a ring or a stored call.
 * @returns {string|undefined}
 */
export function profilePhotoPath(value) {
    return typeof value === 'string' && PHOTO_PATH.test(value) ? value : undefined;
}
/**
 * A full international number, or undefined.
 *
 * @param {unknown} value - A ring's callerPhone.
 * @returns {string|undefined}
 */
export function e164Phone(value) {
    return typeof value === 'string' && E164.test(value) ? value : undefined;
}
/**
 * The photo's address for an image view: the link on the API origin.
 *
 * @param {string|undefined} path - A profile photo link.
 * @param {string} origin - The API origin ('' on the web, where the page is on it).
 * @returns {string|undefined}
 */
export function profilePhotoUri(path, origin) {
    const photo = profilePhotoPath(path);
    if (!photo) {
        return undefined;
    }
    return `${origin.replace(/\/+$/, '')}${photo}`;
}
/**
 * Remembers the other person's photo for a call from its ring.
 *
 * @param {string|undefined} callId - The call.
 * @param {unknown} path - The ring's photo link.
 * @returns {void}
 */
export function rememberCallPhoto(callId, path) {
    const photo = profilePhotoPath(path);
    if (!callId || !photo) {
        return;
    }
    callPhotos.delete(callId);
    callPhotos.set(callId, photo);
    while (callPhotos.size > MAX_REMEMBERED) {
        callPhotos.delete(callPhotos.keys().next().value);
    }
}
/**
 * @param {string|undefined} callId - The call.
 * @returns {string|undefined} The other person's photo link from its ring.
 */
export function rememberedCallPhoto(callId) {
    return callId ? callPhotos.get(callId) : undefined;
}
